/* globals am4core, am4charts, am4themes_animated, am4plugins_sliceGrouper */

export default class ChartsV4 {
  static ready(callback) {
    am4core.ready(() => {
      am4core.useTheme(am4themes_animated);
      callback();
    });
  }

  static makeChart(id, type, stats, hideLegend, isRotated) {
    const chart = am4core.create(id, type);
    chart.hiddenState.properties.opacity = 0;
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineY.disabled = true;
    chart.cursor.lineX.disabled = true;
    chart.cursor.behavior = isRotated ? 'zoomX' : 'zoomY';
    // Format graphs number
    chart.language.locale._decimalSeparator = '.';
    chart.language.locale._thousandSeparator = ' ';

    if (!hideLegend) {
      chart.legend = new am4charts.Legend();
      chart.legend.position = 'top';
    }
    chart.data = stats;
    return chart;
  }

  static makeCategoryAxis(
    chart,
    category,
    alternateLabels,
    isRotated,
    closerColumns,
  ) {
    const axes = isRotated ? chart.yAxes : chart.xAxes;
    const axis = axes.push(new am4charts.CategoryAxis());
    axis.dataFields.category = category;
    axis.renderer.minGridDistance = isRotated ? 1 : 50;
    if (closerColumns) {
      // IF TRUE PUTS THE 2 COLUMNS SIDE BY SIDE
      axis.renderer.cellStartLocation = 0.2;
      axis.renderer.cellEndLocation = 0.8;
    }
    if (alternateLabels && !isRotated) {
      axis.renderer.labels.template.adapter.add('dy', (dy, target) => {
        // eslint-disable-next-line no-bitwise
        if (target.dataItem && target.dataItem.index & true) {
          return dy + 20;
        }
        return dy;
      });
    }
    return axis;
  }

  static makeValueOppositeAxis(chart, label, limits) {
    const valueAxisRight = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxisRight.title.text = label;
    valueAxisRight.renderer.opposite = true;
    if (limits) {
      valueAxisRight.min = limits.min;
      valueAxisRight.max = limits.max;
    }
    return valueAxisRight;
  }

  static makeValueAxis(chart, label, limits, isRotated) {
    const axes = isRotated ? chart.xAxes : chart.yAxes;
    const axis = axes.push(new am4charts.ValueAxis());
    axis.title.text = label;
    if (limits) {
      axis.min = limits.min;
      axis.max = limits.max;
    }
    return axis;
  }

  static makeColumnSeries(
    is3D,
    name,
    value,
    category,
    notClustered,
    isStacked,
    isRotated,
    colorRect,
    closerColumns, // if true puts the 2 columns side by side
    layeredColumns, // if true layered columns
    columnRatio = 1, // identify multiple columns sizes
    unity = '',
    addEventListener = null,
    isCrypto = false,
  ) {
    const series = is3D
      ? new am4charts.ColumnSeries3D()
      : new am4charts.ColumnSeries();
    series.name = name;
    if (notClustered) {
      series.clustered = false;
    }
    if (isStacked) {
      series.stacked = true;
    }
    const valueKey = isRotated ? 'valueX' : 'valueY';
    series.dataFields[valueKey] = value;
    const categoryKey = isRotated ? 'categoryY' : 'categoryX';
    series.dataFields[categoryKey] = category;
    series.tooltip.pointerOrientation = 'vertical';
    series.tooltipText = `${name}: [bold]{${value}.formatNumber('#,###.00')}[/]`;
    if (unity?.length > 0) {
      series.tooltipText = !isCrypto
        ? `${name}: [bold]{${value}.formatNumber('#,###.00')} ${unity}[/]`
        : `${name}: [bold]{${value}} ${unity}[/]`;
    }
    if (addEventListener) {
      series.columns.template.events.on('hit', event => {
        addEventListener(event);
      });
    }
    if (colorRect.length > 0) {
      series.fill = am4core.color(colorRect);
    }
    if (closerColumns) {
      // all width occupied
      series.columns.template.width = am4core.percent(100);
    }
    if (layeredColumns) {
      series.clustered = false;
    }
    if (layeredColumns && columnRatio) {
      const fullWidth = closerColumns ? 100 : 90;
      series.columns.template.width = am4core.percent(columnRatio * fullWidth);
    }
    series.columns.template.column.fillOpacity = 0.75;
    const hoverState = series.columns.template.column.states.create('hover');
    hoverState.properties.fillOpacity = 1;

    return series;
  }

  static makeLineSeries(
    name,
    value,
    category,
    isRotated,
    colorLine,
    bullet,
    unity,
    isCrypto = false,
  ) {
    const series = new am4charts.LineSeries();
    series.name = name;
    const valueKey = isRotated ? 'valueX' : 'valueY';
    series.dataFields[valueKey] = value;
    const categoryKey = isRotated ? 'categoryY' : 'categoryX';
    series.dataFields[categoryKey] = category;
    if (colorLine) {
      series.stroke = am4core.color(colorLine); // Line color
      series.fill = am4core.color(colorLine); // Info Box color
    }
    if (bullet) {
      // Apply a dot (bullet) in the line
      series.bullets.push(new am4charts.CircleBullet());
    }
    series.strokeWidth = 3;
    series.tooltip.pointerOrientation = 'vertical';
    series.tooltipText = `${name}: [bold]{${value}.formatNumber('#,###.00')}[/]`;
    if (unity?.length > 0) {
      series.tooltipText = !isCrypto
        ? `${name}: [bold]{${value}.formatNumber('#,###.00')} ${unity}[/]`
        : `${name}: [bold]{${value}} ${unity}[/]`;
    }
    return series;
  }

  static columnAndLine2Axis(stats, id, config) {
    const defaultConfig = {
      category: '',
      yLabel: '',
      columnValueKey: '',
      columnSeriesName: '',
      colorRect: '',
      y2Label: '',
      lineValueKey: '',
      lineSeriesName: '',
      colorLine: '',
      valueAxisLimits: { min: 0 },
      isRotated: false,
      wrapXLabels: {
        truncate: false,
        wrap: false,
        maxWidth: 0,
      },
      unity: '',
      bullet: false,
    };

    const _config = { ...defaultConfig, ...config };

    const hasLineSeries = _config.lineSeriesName && _config.lineValueKey;
    const hasColumnSeries = _config.columnSeriesName && _config.columnValueKey;
    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart,
      stats,
      !hasLineSeries || !hasColumnSeries,
      _config.isRotated,
    );
    // Bottom Axis
    ChartsV4.makeCategoryAxis(
      chart,
      _config.category,
      true,
      _config.isRotated,
      _config.wrapXLabels,
    );
    // Left Axis
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );
    // Right Axis
    const axis2 = ChartsV4.makeValueOppositeAxis(
      chart,
      _config.y2Label,
      _config.valueAxisLimits,
    );
    // BAR
    if (hasColumnSeries) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.columnSeriesName,
          _config.columnValueKey,
          _config.category,
          false,
          false,
          _config.isRotated,
          _config.colorRect,
          false,
          false,
          1,
          _config.unity,
        ),
      );
    }
    // Line linked to right axis
    if (hasLineSeries) {
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config.lineSeriesName,
          _config.lineValueKey,
          _config.category,
          _config.isRotated,
          axis2, // Right Axis
          _config.colorLine,
          _config.unity,
        ),
      );
    }
    return chart;
  }

  static columnAndLine(stats, id, config) {
    const defaultConfig = {
      category: '',
      columnValueKey: '',
      lineValueKey: '',
      columnSeriesName: '',
      lineSeriesName: '',
      yLabel: '',
      valueAxisLimits: {},
      isRotated: false,
      colorRect: '',
      colorLine: '',
      bullet: false,
      unity: '',
      addEventListener: null,
    };
    const _config = { ...defaultConfig, ...config };

    const hasLineSeries = _config.lineSeriesName && _config.lineValueKey;
    const hasColumnSeries = _config.columnSeriesName && _config.columnValueKey;
    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart,
      stats,
      !hasLineSeries || !hasColumnSeries,
      _config.isRotated,
    );
    ChartsV4.makeCategoryAxis(chart, _config.category, true, _config.isRotated);
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );
    if (hasColumnSeries) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.columnSeriesName,
          _config.columnValueKey,
          _config.category,
          false,
          false,
          _config.isRotated,
          _config.colorRect,
          false,
          false,
          1,
          _config.unity,
          _config.addEventListener,
        ),
      );
    }
    if (hasLineSeries) {
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config.lineSeriesName,
          _config.lineValueKey,
          _config.category,
          _config.isRotated,
          _config.colorLine,
          _config.bullet,
          _config.unity,
        ),
      );
    }

    return chart;
  }

  static multiLine(stats, id, config) {
    const defaultConfig = {
      category: '',
      line1ValueKey: '',
      line1SeriesName: '',
      yLabel: '',
      valueAxisLimits: {},
      isRotated: false,
      wrapXLabels: {
        truncate: false,
        wrap: false,
        maxWidth: 0,
      },
      bullet: false,
      unity: '',
      addEventListener: null,
    };
    const _config = { ...defaultConfig, ...config };

    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart,
      stats,
      false,
      _config.isRotated,
    );
    ChartsV4.makeCategoryAxis(
      chart,
      _config.category,
      true,
      _config.isRotated,
      _config.wrapXLabels,
      false,
    );
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );

    // We max at 10 lines per graph, could be evaluated if more is needed at some point
    for (let i = 1; i <= 10; i += 1) {
      if (!_config[`line${i}ValueKey` || !_config[`line${i}SeriesName`]]) {
        break;
      }
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config[`line${i}SeriesName`],
          _config[`line${i}ValueKey`],
          _config.category,
          _config.isRotated,
          false,
          false,
          _config.bullet,
          _config.unity || '',
        ),
      );
    }

    return chart;
  }

  // Layered Columns
  static layeredColumns(stats, id, config) {
    const defaultConfig = {
      category: '',
      columnValueKey: '',
      column2ValueKey: '',
      lineValueKey: '',
      columnSeriesName: '',
      column2SeriesName: '',
      lineSeriesName: '',
      line2SeriesName: '',
      line2ValueKey: '',
      color2Line: '',
      yLabel: '',
      valueAxisLimits: {},
      isRotated: false,
      colorRect: '',
      colorLine: '',
      colorRect2: '',
      closerColumns: 'false', // PUT COLUMNS SIDE BY SIDE IF TRUE
      unity: '',
      historicalColumnValueKey: '',
      historicalColumnSeriesName: '',
      colorHistorical: '',
      isCrypto: false,
      isStacked: false, // will not applied layered if true
      showLegend: false,
    };
    const _config = { ...defaultConfig, ...config };

    const hasLineSeries = _config.lineSeriesName && _config.lineValueKey;
    const hasLine2Series = _config.line2SeriesName && _config.line2ValueKey;
    const hasColumnSeries = _config.columnSeriesName && _config.columnValueKey;
    const hasColumn2Series =
      _config.column2SeriesName && _config.column2ValueKey;
    const hasHistoricalColumnSeries =
      _config.historicalColumnSeriesName && _config.historicalColumnValueKey;
    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart,
      stats,
      !hasLineSeries || !hasColumnSeries,
      _config.isRotated,
    );
    ChartsV4.makeCategoryAxis(
      chart,
      _config.category,
      true,
      _config.isRotated,
      false,
      _config.closerColumns,
    );
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );
    if (hasHistoricalColumnSeries) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.historicalColumnSeriesName,
          _config.historicalColumnValueKey,
          _config.category,
          false,
          _config.isStacked,
          _config.isRotated,
          _config.colorHistorical,
          _config.closerColumns,
          !_config.isStacked,
          1,
          _config.unity,
          null,
          _config.isCrypto,
        ),
      );
    }
    if (hasColumnSeries) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.columnSeriesName,
          _config.columnValueKey,
          _config.category,
          false,
          _config.isStacked,
          _config.isRotated,
          _config.colorRect,
          _config.closerColumns,
          !_config.isStacked,
          0.8,
          _config.unity,
          null,
          _config.isCrypto,
        ),
      );
    }
    if (hasColumn2Series) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.column2SeriesName,
          _config.column2ValueKey,
          _config.category,
          false,
          _config.isStacked,
          _config.isRotated,
          _config.colorRect2,
          _config.closerColumns,
          !_config.isStacked,
          0.6,
          _config.unity,
          null,
          _config.isCrypto,
        ),
      );
    }
    if (hasLineSeries) {
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config.lineSeriesName,
          _config.lineValueKey,
          _config.category,
          _config.isRotated,
          _config.colorLine,
          true,
          _config.unity,
          _config.isCrypto,
        ),
      );
    }

    if (hasLine2Series) {
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config.line2SeriesName,
          _config.line2ValueKey,
          _config.category,
          _config.isRotated,
          _config.color2Line,
          true,
          _config.unity,
          _config.isCrypto,
        ),
      );
    }

    if (_config.showLegend) {
      chart.legend = new am4charts.Legend();
      chart.legend.position = 'bottom';
      chart.legend.contentAlign = 'left';
    }

    return chart;
  }

  // TWO COLUMNS FOR SAME CATEGORY
  static twoColumnAndLine(stats, id, config) {
    const defaultConfig = {
      category: '',
      columnValueKey: '',
      column2ValueKey: '',
      lineValueKey: '',
      columnSeriesName: '',
      column2SeriesName: '',
      lineSeriesName: '',
      yLabel: '',
      valueAxisLimits: { min: 0 },
      isRotated: false,
      colorRect: '',
      colorLine: '',
      colorRect2: '',
      closerColumns: 'false', // PUT COLUMNS SIDE BY SIDE IF TRUE
      unity: '',
    };
    const _config = { ...defaultConfig, ...config };

    const hasLineSeries = _config.lineSeriesName && _config.lineValueKey;
    const hasColumnSeries = _config.columnSeriesName && _config.columnValueKey;
    const hasColumn2Series =
      _config.column2SeriesName && _config.column2ValueKey;
    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart,
      stats,
      !hasLineSeries || !hasColumnSeries,
      _config.isRotated,
    );
    ChartsV4.makeCategoryAxis(
      chart,
      _config.category,
      true,
      _config.isRotated,
      _config.closerColumns,
    );
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );
    if (hasColumnSeries) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.columnSeriesName,
          _config.columnValueKey,
          _config.category,
          false,
          true,
          _config.isRotated,
          _config.colorRect,
          _config.closerColumns,
          false,
          1,
          _config.unity,
        ),
      );
    }
    if (hasColumn2Series) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          false,
          _config.column2SeriesName,
          _config.column2ValueKey,
          _config.category,
          false,
          true,
          _config.isRotated,
          _config.colorRect2,
          _config.closerColumns,
          false,
          1,
          _config.unity,
        ),
      );
    }
    if (hasLineSeries) {
      chart.series.push(
        ChartsV4.makeLineSeries(
          _config.lineSeriesName,
          _config.lineValueKey,
          _config.category,
          _config.isRotated,
          _config.colorLine,
          true,
          _config.unity,
        ),
      );
    }

    return chart;
  }

  static columns3D(stats, id, config) {
    const defaultConfig = {
      category: '',
      yLabel: '',
      column1Name: '',
      column1Value: '',
      column2Name: '',
      column2Value: '',
      valueAxisLimits: { min: 0 },
      isRotated: false,
    };
    const _config = { ...defaultConfig, ...config };

    const hasColumn2Series = _config.column2Name && _config.column2Value;
    const chart = ChartsV4.makeChart(
      id,
      am4charts.XYChart3D,
      stats,
      !hasColumn2Series,
      _config.isRotated,
    );
    chart.angle = 40;
    chart.depth = hasColumn2Series ? 40 : 20;
    ChartsV4.makeCategoryAxis(chart, _config.category, true, _config.isRotated);
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.valueAxisLimits,
      _config.isRotated,
    );
    chart.series.push(
      ChartsV4.makeColumnSeries(
        true,
        _config.column1Name,
        _config.column1Value,
        _config.category,
        true,
        false,
        _config.isRotated,
      ),
    );
    if (hasColumn2Series) {
      chart.series.push(
        ChartsV4.makeColumnSeries(
          true,
          _config.column2Name,
          _config.column2Value,
          _config.category,
          true,
          _config.isRotated,
        ),
      );
    }

    return chart;
  }

  static pie(stats, id, config) {
    const defaultConfig = {
      category: 'title',
      value: 'value',
      customLabel: '',
      groupThreshold: 0,
      donutPercentage: 0,
      addEventListener: null,
    };
    const _config = { ...defaultConfig, ...config };

    const chart = am4core.create(id, am4charts.PieChart);
    chart.data = stats;

    const series = chart.series.push(new am4charts.PieSeries());
    if (_config.customLabel) {
      series.labels.template.text = _config.customLabel;
    }
    series.dataFields.value = _config.value;
    series.dataFields.category = _config.category;

    if (_config.addEventListener) {
      series.slices.template.events.on('hit', event => {
        _config.addEventListener(event);
      });
    }

    if (_config.groupThreshold) {
      const grouper = series.plugins.push(
        new am4plugins_sliceGrouper.SliceGrouper(),
      );
      grouper.threshold = _config.groupThreshold;
      grouper.groupName = '';
      grouper.clickBehavior = 'break';
    } else {
      const hideSmall = (hidden, target) =>
        target.dataItem.values.value.percent < 1;

      series.ticks.template.adapter.add('hidden', hideSmall);
      series.labels.template.adapter.add('hidden', hideSmall);
    }

    if (_config.donutPercentage) {
      chart.innerRadius = am4core.percent(_config.donutPercentage); // Donut hole size
    }

    return chart;
  }

  static donutWithLabel(stats, id, config) {
    const defaultConfig = {
      category: 'title',
      value: 'value',
      customLabel: '',
      labelColor: '',
      labelFontSize: 0,
      donutPercentage: 0,
    };
    const _config = { ...defaultConfig, ...config };

    const chart = am4core.create(id, am4charts.PieChart); // Create Chart
    chart.data = stats; // Chart data by stats props

    const series = chart.series.push(new am4charts.PieSeries()); // Create Fields
    series.dataFields.value = _config.value; // Value field
    series.dataFields.category = _config.category; // Value Legend
    // Disabled the labels and ticks that usually appear in pie donut
    series.labels.template.disabled = true;
    series.ticks.template.disabled = true;

    chart.legend = new am4charts.Legend(); // Create Legend
    chart.legend.position = 'right'; // Right side
    chart.innerRadius = am4core.percent(_config.donutPercentage); // Donut hole size

    // Center Label
    const label = series.createChild(am4core.Label);
    label.text = _config.customLabel; // Text
    // Orientation
    label.horizontalCenter = 'middle';
    label.verticalCenter = 'middle';

    label.fontSize = _config.labelFontSize; // FontSize
    label.fill = am4core.color(_config.labelColor); // Color

    return chart;
  }

  static radar(stats, id, config) {
    const defaultConfig = {
      category: 'title',
      value: 'value',
      axisLimits: { min: 0 },
    };
    const _config = { ...defaultConfig, ...config };

    const chart = am4core.create(id, am4charts.RadarChart);
    chart.data = stats;

    const categoryAxis = ChartsV4.makeCategoryAxis(chart, _config.category);
    categoryAxis.renderer.gridType = 'polygons';
    categoryAxis.renderer.labels.template.fontSize = 11;

    const valueAxis = ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.axisLimits,
    );
    valueAxis.renderer.gridType = 'polygons';

    const series = chart.series.push(new am4charts.RadarSeries());
    series.connect = true;
    series.connectEnds = true;
    series.fillOpacity = 0.2;
    series.dataFields.valueY = _config.value;
    series.dataFields.categoryX = _config.category;

    return chart;
  }

  static gauge(stats, id, config) {
    const defaultConfig = {
      category: 'title',
      value: 'value',
      axisLimits: { min: 0, max: 100 },
    };
    const _config = { ...defaultConfig, ...config };

    const chart = am4core.create(id, am4charts.RadarChart);
    chart.startAngle = -90;
    chart.endAngle = 180;
    chart.innerRadius = am4core.percent(20);
    chart.data = (stats || []).map(s => ({
      ...s,
      max: _config.axisLimits.max,
    }));

    const categoryAxis = ChartsV4.makeCategoryAxis(
      chart,
      _config.category,
      false,
      true,
    );
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.labels.template.horizontalCenter = 'right';
    categoryAxis.renderer.labels.template.fontWeight = 500;
    categoryAxis.renderer.labels.template.fontSize = 11;
    categoryAxis.renderer.labels.template.truncate = true;
    categoryAxis.renderer.labels.template.tooltipText = '{category}';
    categoryAxis.renderer.labels.template.adapter.add('fill', (fill, target) =>
      target.dataItem.index >= 0
        ? chart.colors.getIndex(target.dataItem.index)
        : fill,
    );
    categoryAxis.renderer.minGridDistance = 10;
    categoryAxis.events.on('sizechanged', ev => {
      const axis = ev.target;
      axis.renderer.labels.template.maxWidth = axis.pixelWidth - 30;
    });

    const valueAxis = ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.axisLimits,
      true,
    );
    valueAxis.renderer.grid.template.strokeOpacity = 0;
    valueAxis.renderer.labels.template.fontSize = 11;
    valueAxis.strictMinMax = true;

    const series1 = chart.series.push(new am4charts.RadarColumnSeries());
    series1.dataFields.valueX = 'max';
    series1.dataFields.categoryY = _config.category;
    series1.clustered = false;
    series1.columns.template.fill = new am4core.InterfaceColorSet().getFor(
      'alternativeBackground',
    );
    series1.columns.template.fillOpacity = 0.08;
    series1.columns.template.cornerRadiusTopLeft = 20;
    series1.columns.template.strokeWidth = 0;
    series1.columns.template.radarColumn.cornerRadius = 20;

    const series2 = chart.series.push(new am4charts.RadarColumnSeries());
    series2.dataFields.valueX = _config.value;
    series2.dataFields.categoryY = _config.category;
    series2.clustered = false;
    series2.columns.template.strokeWidth = 0;
    series2.columns.template.tooltipText = '{categoryY}: [bold]{valueX}[/]';
    series2.columns.template.radarColumn.cornerRadius = 20;
    series2.columns.template.adapter.add('fill', (fill, target) =>
      chart.colors.getIndex(target.dataItem.index),
    );

    return chart;
  }

  static chord(stats, id, config) {
    const defaultConfig = {
      fromName: '',
      toName: '',
      value: '',
      hideThreshold: 0.02,
    };
    const _config = { ...defaultConfig, ...config };

    const totalConnections = stats.reduce(
      (total, stat) => total + stat[_config.value],
      0,
    );
    const filteredStats = stats.filter(
      stat => stat[_config.value] / totalConnections > _config.hideThreshold,
    );
    const chart = am4core.create(id, am4charts.ChordDiagram);
    chart.data = filteredStats;
    chart.dataFields.fromName = _config.fromName;
    chart.dataFields.toName = _config.toName;
    chart.dataFields.value = _config.value;

    const nodeTemplate = chart.nodes.template;
    nodeTemplate.readerTitle = '';
    nodeTemplate.showSystemTooltip = true;
    nodeTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;

    return chart;
  }

  static barWithAvatar(stats, id, config) {
    const defaultConfig = {
      category: '',
      value: '',
      imageUrl: '',
      yLabel: '',
    };
    const _config = { ...defaultConfig, ...config };

    const chart = ChartsV4.makeChart(id, am4charts.XYChart, stats, true, true);
    chart.paddingRight = 40;

    const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = _config.category;
    categoryAxis.renderer.grid.template.strokeOpacity = 0;
    categoryAxis.renderer.minGridDistance = 10;
    categoryAxis.renderer.labels.template.dx = -20;
    categoryAxis.renderer.minWidth = 150;
    categoryAxis.renderer.tooltip.dx = -20;

    const valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
    valueAxis.title.fillOpacity = 0.8;
    valueAxis.title.dy = 20;
    valueAxis.title.text = _config.yLabel;
    valueAxis.renderer.inside = true;
    valueAxis.renderer.labels.template.fillOpacity = 0.3;
    valueAxis.renderer.grid.template.strokeOpacity = 0;
    valueAxis.min = 0;
    valueAxis.cursorTooltipEnabled = false;
    valueAxis.renderer.baseGrid.strokeOpacity = 0;
    valueAxis.renderer.labels.template.dy = 30;

    const series = chart.series.push(new am4charts.ColumnSeries());
    series.dataFields.valueX = _config.value;
    series.dataFields.categoryY = _config.category;
    series.tooltipText = '{valueX.value}';
    series.tooltip.pointerOrientation = 'vertical';
    series.tooltip.dy = -30;
    series.columnsContainer.zIndex = 100;
    series.mainContainer.mask = undefined;

    const columnTemplate = series.columns.template;
    columnTemplate.height = am4core.percent(50);
    columnTemplate.maxHeight = 35;
    columnTemplate.column.fillOpacity = 0.75;
    columnTemplate.column.cornerRadius(40, 10, 40, 10);
    columnTemplate.strokeOpacity = 0;

    const bullet = columnTemplate.createChild(am4charts.CircleBullet);
    bullet.circle.radius = 20;
    bullet.valign = 'middle';
    bullet.align = 'left';
    bullet.isMeasured = true;
    bullet.interactionsEnabled = false;
    bullet.horizontalCenter = 'right';
    bullet.interactionsEnabled = false;

    bullet.states.create('hover');
    const outlineCircle = bullet.createChild(am4core.Circle);
    outlineCircle.fillOpacity = 0.9;
    outlineCircle.adapter.add('radius', (radius, target) => {
      const circleBullet = target.parent;
      return circleBullet.circle.pixelRadius + 5;
    });

    const image = bullet.createChild(am4core.Image);
    image.width = 40;
    image.height = 40;
    image.horizontalCenter = 'middle';
    image.verticalCenter = 'middle';
    image.propertyFields.href = _config.imageUrl;

    image.adapter.add('mask', (mask, target) => {
      const circleBullet = target.parent;
      return circleBullet.circle;
    });

    let previousBullet;
    chart.cursor.events.on('cursorpositionchanged', () => {
      const dataItem = series.tooltipDataItem;

      if (dataItem.column) {
        const newBullet = dataItem.column.children.getIndex(1);

        if (previousBullet && previousBullet !== newBullet) {
          previousBullet.isHover = false;
        }

        if (previousBullet !== newBullet) {
          const hs = newBullet.states.getKey('hover');
          hs.properties.dx = dataItem.column.pixelWidth;
          newBullet.isHover = true;

          previousBullet = newBullet;
        }
      }
    });

    return chart;
  }

  static columnWithPieBullet(stats, id, config) {
    const defaultConfig = {
      yLabel: '',
      columnCategory: '',
      columnValue: '',
      pieCategory: '',
      pieValue: '',
      subData: 'pie',
      limits: { min: 0 },
      isRotated: true,
    };
    const _config = { ...defaultConfig, ...config };

    const chart = ChartsV4.makeChart(id, am4charts.XYChart, stats, true, false);
    ChartsV4.makeCategoryAxis(
      chart,
      _config.columnCategory,
      true,
      _config.isRotated,
    );
    ChartsV4.makeValueAxis(
      chart,
      _config.yLabel,
      _config.limits,
      _config.isRotated,
    );

    const series = chart.series.push(
      ChartsV4.makeColumnSeries(
        false,
        _config.yLabel,
        _config.columnValue,
        _config.columnCategory,
        false,
        false,
        _config.isRotated,
      ),
    );

    const columnTemplate = series.columns.template;
    columnTemplate.column.tooltipText = `{${_config.columnCategory}}: {${_config.columnValue}}`;
    columnTemplate.column.tooltipY = 0;
    if (_config.isRotated) {
      columnTemplate.column.cornerRadiusBottomRight = 5;
      columnTemplate.column.cornerRadiusTopRight = 5;
    } else {
      columnTemplate.column.cornerRadiusTopLeft = 5;
      columnTemplate.column.cornerRadiusTopRight = 5;
    }
    columnTemplate.strokeOpacity = 0;

    const pieChart = series.columns.template.createChild(am4charts.PieChart);
    pieChart.width = am4core.percent(100);
    pieChart.height = am4core.percent(100);
    pieChart.align = 'center';
    pieChart.valign = 'middle';
    pieChart.dataFields.data = _config.subData;

    const pieSeries = pieChart.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = _config.pieValue;
    pieSeries.dataFields.category = _config.pieCategory;
    pieSeries.labels.template.disabled = true;
    pieSeries.ticks.template.disabled = true;
    pieSeries.slices.template.stroke = am4core.color('#ffffff');
    pieSeries.slices.template.strokeWidth = 1;
    pieSeries.slices.template.strokeOpacity = 0;

    pieSeries.slices.template.adapter.add('fill', () =>
      am4core.color('#ffffff'),
    );

    pieSeries.slices.template.adapter.add(
      'fillOpacity',
      (fillOpacity, target) => (target.dataItem.index + 1) * 0.2,
    );

    pieSeries.hiddenState.properties.startAngle = -90;
    pieSeries.hiddenState.properties.endAngle = 270;

    return chart;
  }

  static gantt(stats, id, config) {
    const defaultConfig = {
      category: '',
      fromDate: 'from',
      toDate: 'to',
      colorField: 'color',
      dateFormat: 'dd-MM-yyyy HH:mm',
      tooltip: '{categoryY}: {openDateX} - {dateX}',
    };
    const _config = { ...defaultConfig, ...config };

    const chart = ChartsV4.makeChart(id, am4charts.XYChart, stats, true, true);
    chart.paddingRight = 30;
    chart.dateFormatter.inputDateFormat = _config.dateFormat;

    chart.data = stats;

    const categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = _config.category;
    categoryAxis.renderer.grid.template.location = 0;
    categoryAxis.renderer.inversed = true;

    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.dateFormatter.dateFormat = _config.dateFormat;
    dateAxis.renderer.minGridDistance = 30;
    dateAxis.baseInterval = { count: 1, timeUnit: 'hour' };
    dateAxis.renderer.tooltipLocation = 0;

    const series1 = chart.series.push(new am4charts.ColumnSeries());
    series1.columns.template.width = am4core.percent(80);
    series1.columns.template.tooltipText = _config.tooltip;

    series1.dataFields.openDateX = _config.fromDate;
    series1.dataFields.dateX = _config.toDate;
    series1.dataFields.categoryY = _config.category;
    series1.columns.template.propertyFields.fill = _config.colorField;
    series1.columns.template.propertyFields.stroke = _config.colorField;
    series1.columns.template.strokeOpacity = 1;

    return chart;
  }
}
